// components/AtcCodeMaintenance.jsx
//
// Superadmin maintenance for the BIR ATC reference table.
//
// Why this screen exists: 124 of the 132 seeded rows came from a third-party
// source (NetSuite localization docs) rather than a BIR issuance, and every
// final-withholding (2306) rate is uncorroborated. Those rates auto-fill the
// withholding amount on 2306/2307 entry, so an unreviewed row reaches a filing.
// The "Needs review" badge is the point of the screen — it is what makes the
// untrusted rows visible before someone files with them.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import atcCodeAPI from '../api/atcCodeAPI';
import useGuardedMutation from '../hooks/useGuardedMutation';
import DataTable from './DataTable';
import Button from './Button';
import ConfirmModal from './ConfirmModal';
import notify from '../lib/notify';

const FORM_TYPES = ['2307', '2306', '1601EQ'];
const PAYEE_CLASSES = ['any', 'individual', 'corporation'];

const EMPTY_FORM = {
  code: '',
  description: '',
  forms: ['2307'],
  ratePercent: '',
  payeeClass: 'any',
  issuance: '',
  effectiveFrom: '',
  effectiveTo: '',
  isActive: true,
};

const fmtDate = (d) => (d ? format(new Date(d), 'yyyy-MM-dd') : '—');
const toDateInput = (d) => (d ? format(new Date(d), 'yyyy-MM-dd') : '');

const pillBase = 'text-xs px-2 py-0.5 rounded-md';

const StatusBadge = ({ active }) => (
  <span className={`${pillBase} ${active ? 'bg-success-50 text-success-700' : 'bg-ink-canvas text-ink-body'}`}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

// Uniform tint per DESIGN.md 5.1 — the text differentiates, not the hue.
const FormPill = ({ label }) => (
  <span className={`${pillBase} bg-primary-50 text-primary-700 mr-1`}>{label}</span>
);

const AtcCodeMaintenance = () => {
  const [rows, setRows] = useState([]);
  const [needsReviewCount, setNeedsReviewCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formTypeFilter, setFormTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [reviewOnly, setReviewOnly] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [reviewNote, setReviewNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (formTypeFilter) params.formType = formTypeFilter;
      const { atcCodes, needsReviewCount: count } = await atcCodeAPI.admin.list(params);
      setRows(atcCodes);
      setNeedsReviewCount(count);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to load ATC codes');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [formTypeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toUpperCase();
    return rows.filter((r) => {
      if (!showInactive && !r.isActive) return false;
      if (reviewOnly && !r.needsReview) return false;
      if (!term) return true;
      return (
        String(r.code || '').toUpperCase().includes(term) ||
        String(r.description || '').toUpperCase().includes(term) ||
        String(r.issuance || '').toUpperCase().includes(term)
      );
    });
  }, [rows, search, reviewOnly, showInactive]);

  const openCreate = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormData({
      code: row.code || '',
      description: row.description || '',
      forms: row.forms?.length ? row.forms : ['2307'],
      // The table stores basis points; the form takes a percent because that is
      // how BIR states rates. The server rejects a percent that does not land on
      // a whole basis point rather than rounding it.
      ratePercent: String((row.rateBasisPoints ?? 0) / 100),
      payeeClass: row.payeeClass || 'any',
      issuance: row.issuance || '',
      effectiveFrom: toDateInput(row.effectiveFrom),
      effectiveTo: toDateInput(row.effectiveTo),
      isActive: row.isActive !== false,
    });
    setModalOpen(true);
  };

  const buildPayload = () => ({
    code: formData.code.trim().toUpperCase(),
    description: formData.description.trim(),
    forms: formData.forms,
    ratePercent: Number(formData.ratePercent),
    payeeClass: formData.payeeClass,
    issuance: formData.issuance.trim(),
    effectiveFrom: formData.effectiveFrom,
    effectiveTo: formData.effectiveTo || null,
    isActive: formData.isActive,
  });

  const { mutate: save, isPending: saving } = useGuardedMutation(async () => {
    if (!formData.code.trim()) return notify.error("Couldn't save", 'ATC code is required.');
    if (!formData.description.trim()) return notify.error("Couldn't save", 'Description is required.');
    if (!formData.issuance.trim()) {
      return notify.error(
        "Couldn't save",
        'Issuance is required — record the BIR circular this rate comes from, or say plainly that it is unverified.'
      );
    }
    if (!formData.effectiveFrom) return notify.error("Couldn't save", 'Effective from is required.');
    if (formData.ratePercent === '' || Number.isNaN(Number(formData.ratePercent))) {
      return notify.error("Couldn't save", 'Rate must be a number.');
    }
    if (!formData.forms.length) return notify.error("Couldn't save", 'Select at least one form.');

    try {
      if (editing) {
        await atcCodeAPI.admin.update(editing._id, buildPayload());
        notify.success('Saved', `${formData.code.toUpperCase()} updated.`);
      } else {
        await atcCodeAPI.admin.create(buildPayload());
        notify.success('Created', `${formData.code.toUpperCase()} added.`);
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      notify.error("Couldn't save", typeof err === 'string' ? err : 'Failed to save ATC code');
    }
    return undefined;
  });

  const { mutate: deactivate, isPending: deactivating } = useGuardedMutation(async (row) => {
    try {
      await atcCodeAPI.admin.deactivate(row._id);
      notify.success('Deactivated', `${row.code} is no longer selectable.`);
      setConfirmTarget(null);
      await load();
    } catch (err) {
      notify.error("Couldn't deactivate", typeof err === 'string' ? err : 'Failed to deactivate');
    }
  });

  const { mutate: approve, isPending: approving } = useGuardedMutation(async () => {
    if (reviewNote.trim().length < 3) {
      return notify.error(
        "Couldn't approve",
        'Record what this rate was confirmed against — the server requires it.'
      );
    }
    try {
      await atcCodeAPI.admin.approve(approveTarget._id, reviewNote.trim());
      notify.success('Approved', `${approveTarget.code} is confirmed — the review warning is cleared.`);
      setApproveTarget(null);
      setReviewNote('');
      await load();
    } catch (err) {
      notify.error("Couldn't approve", typeof err === 'string' ? err : 'Failed to approve');
    }
    return undefined;
  });

  const { mutate: revokeApproval, isPending: revoking } = useGuardedMutation(async (row) => {
    try {
      await atcCodeAPI.admin.revokeApproval(row._id);
      notify.success('Approval withdrawn', `${row.code} needs review again.`);
      await load();
    } catch (err) {
      notify.error("Couldn't withdraw", typeof err === 'string' ? err : 'Failed to withdraw approval');
    }
  });

  const toggleForm = (form) => {
    setFormData((prev) => ({
      ...prev,
      forms: prev.forms.includes(form)
        ? prev.forms.filter((f) => f !== form)
        : [...prev.forms, form],
    }));
  };

  const columns = [
    {
      title: 'Code',
      key: 'code',
      render: (row) => <span className="font-mono text-sm font-medium">{row.code}</span>,
    },
    {
      title: 'Description',
      key: 'description',
      render: (row) => (
        // DataTable sets whitespace-nowrap on every td and white-space inherits,
        // so the override lives here. Wrapping replaces the old 70-char
        // truncation — the full description is readable without a tooltip.
        <span className="block max-w-[240px] whitespace-normal break-words text-sm text-ink-body">
          {row.description}
        </span>
      ),
    },
    {
      title: 'Rate',
      key: 'rateBasisPoints',
      render: (row) => (
        <span className="font-mono text-sm tabular-nums">{(row.rateBasisPoints / 100).toFixed(2)}%</span>
      ),
    },
    {
      title: 'Forms',
      key: 'forms',
      render: (row) => <>{(row.forms || []).map((f) => <FormPill key={f} label={f} />)}</>,
    },
    {
      title: 'Payee',
      key: 'payeeClass',
      render: (row) => <span className="text-xs capitalize text-ink-body">{row.payeeClass}</span>,
    },
    {
      title: 'Effectivity',
      key: 'effectiveFrom',
      render: (row) => (
        <span className="text-xs text-ink-body whitespace-nowrap">
          {fmtDate(row.effectiveFrom)} → {row.effectiveTo ? fmtDate(row.effectiveTo) : 'open'}
        </span>
      ),
    },
    {
      // Provenance and the review action are one column: the state badge and the
      // button that changes it belong together. The raw issuance string is not
      // rendered — every NetSuite row repeats the same "NetSuite reference, …"
      // prefix, which is noise at row level. It stays on hover, in the approve
      // modal, and in the audit log.
      title: 'Review',
      key: 'review',
      render: (row) => (
        <div className="flex items-center gap-2 whitespace-normal">
          {row.needsReview && (
            <span
              className={`${pillBase} bg-warning-50 text-warning-700 w-fit shrink-0`}
              title={row.issuance}
            >
              Needs review
            </span>
          )}
          {row.reviewedAt && (
            <span
              className={`${pillBase} bg-success-50 text-success-700 w-fit shrink-0`}
              title={[
                row.issuance,
                row.reviewNote ? `Confirmed against: ${row.reviewNote}` : null,
              ].filter(Boolean).join(' — ')}
            >
              Approved {fmtDate(row.reviewedAt)}
            </span>
          )}
          {row.reviewedAt ? (
            <Button variant="ghost" size="sm" onClick={() => revokeApproval(row)} disabled={revoking}>
              Withdraw
            </Button>
          ) : row.needsReview ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setApproveTarget(row); setReviewNote(''); }}
            >
              Approve
            </Button>
          ) : (
            <span className="text-xs text-ink-body" title={row.issuance}>—</span>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'isActive',
      render: (row) => <StatusBadge active={row.isActive !== false} />,
    },
  ];

  return (
    <div className="space-y-4">
      {needsReviewCount > 0 && (
        <div className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3">
          <p className="text-sm text-warning-900">
            <span className="font-semibold">{needsReviewCount}</span> of {rows.length} rows have a rate
            that was never traced to a BIR issuance. These auto-fill the withholding amount on 2306/2307
            entry — confirm each with the accountant, then <span className="font-semibold">Approve</span> it
            here to clear the warning. Approving records who signed off and what against; it never
            overwrites where the rate came from.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-ink-body mb-1">Form</label>
          <select
            value={formTypeFilter}
            onChange={(e) => setFormTypeFilter(e.target.value)}
            className="border border-ink-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">All forms</option>
            {FORM_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>

        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-ink-body mb-1">Search</label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Code, description or issuance"
            className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-ink-body pb-2">
          <input type="checkbox" checked={reviewOnly} onChange={(e) => setReviewOnly(e.target.checked)} />
          Needs review only
        </label>

        <label className="flex items-center gap-2 text-sm text-ink-body pb-2">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive
        </label>

        <div className="pb-1">
          <Button onClick={openCreate}>Add ATC code</Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </div>
      )}

      <p className="text-xs text-ink-body">
        Showing {visibleRows.length} of {rows.length} rows.
      </p>

      <DataTable
        columns={columns}
        data={visibleRows}
        loading={loading}
        emptyMessage="No ATC codes match these filters."
        onEdit={openEdit}
        onDelete={(row) => setConfirmTarget(row)}
        disableDelete={(row) => row.isActive === false}
        deleteTooltip={(row) => (row.isActive === false ? 'Already inactive' : 'Deactivate')}
        itemsPerPage={25}
      />

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="border-b border-ink-line px-6 py-4">
              <h2 className="text-lg font-semibold text-ink">
                {editing ? `Edit ${editing.code}` : 'Add ATC code'}
              </h2>
              {editing && (
                <p className="mt-1 text-xs text-ink-body">
                  Changing a <em>rate</em> is usually wrong: close this row with an
                  &ldquo;effective to&rdquo; date and add a new one instead, so records filed
                  under the old rate still resolve to it.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-6 py-4">
              <div>
                <label className="block text-xs font-medium text-ink-body mb-1">ATC code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="WI840"
                  className="w-full border border-ink-line rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-ink-body mb-1">Rate (%) *</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  max="100"
                  value={formData.ratePercent}
                  onChange={(e) => setFormData({ ...formData, ratePercent: e.target.value })}
                  placeholder="0.5"
                  className="w-full border border-ink-line rounded px-3 py-2 text-sm text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-ink-body">Stored as basis points. 0.5% = 50bp.</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-ink-body mb-1">Description *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-ink-body mb-1">Forms *</label>
                <div className="flex gap-3 pt-2">
                  {FORM_TYPES.map((f) => (
                    <label key={f} className="flex items-center gap-1 text-sm text-ink-body">
                      <input
                        type="checkbox"
                        checked={formData.forms.includes(f)}
                        onChange={() => toggleForm(f)}
                      />
                      {f}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-ink-body mb-1">Payee class</label>
                <select
                  value={formData.payeeClass}
                  onChange={(e) => setFormData({ ...formData, payeeClass: e.target.value })}
                  className="w-full border border-ink-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {PAYEE_CLASSES.map((p) => (
                    <option key={p} value={p} className="capitalize">{p}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-ink-body mb-1">Issuance *</label>
                <input
                  type="text"
                  value={formData.issuance}
                  onChange={(e) => setFormData({ ...formData, issuance: e.target.value })}
                  placeholder="RMC 036-2026"
                  className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-ink-body">
                  The BIR circular this rate comes from. If there isn&rsquo;t one, say so —
                  text containing &ldquo;UNVERIFIED&rdquo;, &ldquo;UNCONFIRMED&rdquo; or
                  &ldquo;CONFLICTED&rdquo; flags the row as needing review everywhere it appears.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-ink-body mb-1">Effective from *</label>
                <input
                  type="date"
                  value={formData.effectiveFrom}
                  onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
                  className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-ink-body mb-1">Effective to</label>
                <input
                  type="date"
                  value={formData.effectiveTo}
                  onChange={(e) => setFormData({ ...formData, effectiveTo: e.target.value })}
                  className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-ink-body">Blank = still in force.</p>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-ink-body">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  Active (selectable in 2306/2307 dropdowns)
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-ink-line px-6 py-4">
              <Button variant="ghost" onClick={() => { setModalOpen(false); setEditing(null); }}>
                Cancel
              </Button>
              <Button onClick={save} loading={saving} disabled={saving}>
                {editing ? 'Save changes' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {approveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-lg">
            <div className="border-b border-ink-line px-6 py-4">
              <h2 className="text-lg font-semibold text-ink">Approve {approveTarget.code}</h2>
              <p className="mt-1 text-sm text-ink-body">
                {approveTarget.description}
              </p>
              <p className="mt-2 text-sm">
                Rate:{' '}
                <span className="font-mono font-semibold tabular-nums">
                  {(approveTarget.rateBasisPoints / 100).toFixed(2)}%
                </span>
                {' '}on {(approveTarget.forms || []).join(', ')}
              </p>
            </div>

            <div className="px-6 py-4">
              <p className="mb-3 rounded border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-900">
                Current provenance: <span className="font-medium">{approveTarget.issuance}</span>
                <br />
                This text is kept as-is. Approving adds your sign-off beside it.
              </p>

              <label className="block text-xs font-medium text-ink-body mb-1">
                Confirmed against *
              </label>
              <textarea
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                rows={3}
                placeholder="e.g. RR 11-2018 Annex A; or confirmed by J. Santos, CPA, 2026-08-25"
                className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-ink-body">
                Required. This is the evidence trail — if the rate is ever questioned, this is
                what shows why it was trusted.
              </p>
            </div>

            <div className="flex justify-end gap-2 border-t border-ink-line px-6 py-4">
              <Button
                variant="ghost"
                onClick={() => { setApproveTarget(null); setReviewNote(''); }}
              >
                Cancel
              </Button>
              <Button onClick={approve} loading={approving} disabled={approving}>
                Approve rate
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={Boolean(confirmTarget)}
        title="Deactivate ATC code?"
        message={
          confirmTarget
            ? `${confirmTarget.code} will stop appearing in the 2306/2307 dropdowns. Existing records that already reference it keep resolving, so nothing filed is affected.`
            : ''
        }
        confirmLabel="Deactivate"
        onConfirm={() => deactivate(confirmTarget)}
        onCancel={() => setConfirmTarget(null)}
        loading={deactivating}
      />
    </div>
  );
};

export default AtcCodeMaintenance;
